import { ActionIcon, Avatar, Badge, Button, CopyButton, Group, Menu, Modal, Paper, Stack, Table, Tabs, Text, Textarea, TextInput, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconCheck, IconCopy, IconDots, IconEdit, IconMailForward, IconPlus, IconSearch, IconTrash, IconUserPlus } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRates } from '@/api/catalog';
import type { Schemas } from '@/api/client';
import { useDeleteGroup, useGroups, useInviteMembers, useMembers, useRemoveMember, useResendInvitation, useSaveGroup, type Member, type UserGroup } from '@/api/workspace';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { PageHeader } from '@/components/PageHeader';
import { RateField } from '@/components/RateField';
import { useApiError } from '@/hooks/useApiError';
import { colorFor, initials } from '@/lib/format';
import { MemberDrawer } from './MemberDrawer';

/** Team: Mitglieder und Gruppen (TEAM-01…05). */
export function TeamPage() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const [inviteOpen, setInviteOpen] = useState(false);
  return (
    <>
      <PageHeader title={t('nav.items.team')}>
        {ctx.isAdmin && (
          <Button leftSection={<IconUserPlus size={16} />} onClick={() => setInviteOpen(true)}>
            {t('team.invite')}
          </Button>
        )}
      </PageHeader>
      <Tabs defaultValue="members">
        <Tabs.List mb="md">
          <Tabs.Tab value="members">{t('team.members')}</Tabs.Tab>
          <Tabs.Tab value="groups">{t('team.groups')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="members">
          <MembersTab />
        </Tabs.Panel>
        <Tabs.Panel value="groups">
          <GroupsTab />
        </Tabs.Panel>
      </Tabs>
      <InviteModal opened={inviteOpen} onClose={() => setInviteOpen(false)} />
    </>
  );
}

function MembersTab() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { data: members = [] } = useMembers(ctx.workspaceId);
  const { data: groups = [] } = useGroups(ctx.workspaceId);
  const { data: rates = [] } = useRates(ctx.workspaceId, ctx.canSeeAmounts);
  const remove = useRemoveMember(ctx.workspaceId);
  const resend = useResendInvitation(ctx.workspaceId);
  const onError = useApiError();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Member | null>(null);
  const visible = members.filter((m) => `${m.name} ${m.email}`.toLowerCase().includes(search.trim().toLowerCase()));

  const roleBadges = (m: Member) =>
    m.roles.map((r) => (
      <Badge key={r} size="xs" variant="light" color={r === 'OWNER' ? 'orange' : r === 'ADMIN' ? 'blue' : 'grape'}>
        {t(`team.roles.${r}`)}
      </Badge>
    ));

  return (
    <>
      <TextInput leftSection={<IconSearch size={14} />} placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.currentTarget.value)} mb="md" w={280} />
      <Paper withBorder>
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('team.name')}</Table.Th>
                <Table.Th>{t('team.role')}</Table.Th>
                <Table.Th>{t('team.groups')}</Table.Th>
                {ctx.canSeeAmounts && <Table.Th>{t('rates.billableRate')}</Table.Th>}
                <Table.Th>{t('team.capacity')}</Table.Th>
                <Table.Th w={50} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {visible.map((m) => (
                <Table.Tr key={m.membershipId} opacity={m.status === 'INACTIVE' ? 0.5 : 1}>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Avatar size={32} radius="xl" color={colorFor(m.userId ?? m.email)}>
                        {initials(m.name)}
                      </Avatar>
                      <div>
                        <Group gap={6}>
                          <Text size="sm" fw={500}>
                            {m.name}
                          </Text>
                          {m.status === 'INVITED' && <Badge size="xs" color="yellow">{t('team.invited')}</Badge>}
                          {m.status === 'INACTIVE' && <Badge size="xs" color="gray">{t('team.inactive')}</Badge>}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {m.email}
                        </Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>{m.roles.length ? roleBadges(m) : <Text size="xs" c="dimmed">{t('team.roles.USER')}</Text>}</Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {m.groupIds
                        .map((id) => groups.find((g) => g.id === id)?.name)
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </Text>
                  </Table.Td>
                  {ctx.canSeeAmounts && (
                    <Table.Td>
                      {m.userId && (
                        <RateField rates={rates} kind="BILLABLE" level="MEMBER" refId={m.userId} placeholder={t('rates.workspaceDefault')} disabled={!ctx.isAdmin} w={120} />
                      )}
                    </Table.Td>
                  )}
                  <Table.Td>
                    <Text size="sm">{(m.capacityMinutes / 60).toLocaleString(ctx.prefs.locale)} h</Text>
                  </Table.Td>
                  <Table.Td>
                    {ctx.isAdmin && (
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" aria-label={t('common.more')}>
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => setEditing(m)}>
                            {t('common.edit')}
                          </Menu.Item>
                          {m.status === 'INVITED' && (
                            <Menu.Item
                              leftSection={<IconMailForward size={14} />}
                              onClick={() =>
                                resend.mutate(m.membershipId, {
                                  onSuccess: () => notifications.show({ color: 'green', message: t('team.invitationResent', { email: m.email }) }),
                                  onError,
                                })
                              }
                            >
                              {t('team.resendInvitation')}
                            </Menu.Item>
                          )}
                          {!m.roles.includes('OWNER') && (
                            <Menu.Item
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              onClick={() =>
                                modals.openConfirmModal({
                                  title: m.status === 'INVITED' ? t('team.cancelInvitation') : t('team.deactivate'),
                                  children: <Text size="sm">{t('team.removeText', { name: m.name })}</Text>,
                                  labels: { confirm: t('common.confirm'), cancel: t('common.cancel') },
                                  confirmProps: { color: 'red' },
                                  onConfirm: () => remove.mutate(m.membershipId, { onError }),
                                })
                              }
                            >
                              {m.status === 'INVITED' ? t('team.cancelInvitation') : t('team.deactivate')}
                            </Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
      <MemberDrawer member={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function GroupsTab() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { data: groups = [] } = useGroups(ctx.workspaceId);
  const { data: members = [] } = useMembers(ctx.workspaceId);
  const save = useSaveGroup(ctx.workspaceId);
  const remove = useDeleteGroup(ctx.workspaceId);
  const onError = useApiError();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<UserGroup | null>(null);

  return (
    <>
      {ctx.isAdmin && (
        <Group mb="md" gap="xs">
          <TextInput placeholder={t('team.groupName')} value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <Button leftSection={<IconPlus size={16} />} onClick={() => name.trim() && save.mutate({ name }, { onSuccess: () => setName(''), onError })}>
            {t('team.createGroup')}
          </Button>
        </Group>
      )}
      <Paper withBorder maw={800}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('team.group')}</Table.Th>
              <Table.Th>{t('team.members')}</Table.Th>
              <Table.Th w={90} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groups.map((g) => (
              <Table.Tr key={g.id}>
                <Table.Td fw={500}>{g.name}</Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {g.membershipIds
                      .map((id) => members.find((m) => m.membershipId === id)?.name)
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {ctx.isAdmin && (
                    <Group gap={4}>
                      <ActionIcon variant="subtle" color="gray" onClick={() => setEditing(g)} aria-label={t('common.edit')}>
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon variant="subtle" color="red" onClick={() => remove.mutate(g.id, { onError })} aria-label={t('common.delete')}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
      <GroupModal group={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function GroupModal({ group, onClose }: { group: UserGroup | null; onClose: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { data: members = [] } = useMembers(ctx.workspaceId);
  const save = useSaveGroup(ctx.workspaceId);
  const onError = useApiError();
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [lastGroup, setLastGroup] = useState<string | null>(null);
  if (group && group.id !== lastGroup) {
    setLastGroup(group.id);
    setName(group.name);
    setSelected(group.membershipIds);
  }
  return (
    <Modal opened={!!group} onClose={() => { setLastGroup(null); onClose(); }} title={t('team.editGroup')}>
      <Stack>
        <TextInput label={t('team.groupName')} value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <MemberMultiSelect members={members} value={selected} onChange={setSelected} label={t('team.members')} />
        <Button
          loading={save.isPending}
          onClick={() => group && save.mutate({ id: group.id, name, membershipIds: selected }, { onSuccess: () => { setLastGroup(null); onClose(); }, onError })}
        >
          {t('common.save')}
        </Button>
      </Stack>
    </Modal>
  );
}

export function MemberMultiSelect({ members, value, onChange, label }: { members: Member[]; value: string[]; onChange: (v: string[]) => void; label: string }) {
  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        {label}
      </Text>
      <Paper withBorder p="xs" mah={240} style={{ overflow: 'auto' }}>
        <Stack gap={4}>
          {members
            .filter((m) => m.status !== 'INACTIVE')
            .map((m) => (
              <Group key={m.membershipId} gap="xs" style={{ cursor: 'pointer' }} onClick={() => onChange(value.includes(m.membershipId) ? value.filter((v) => v !== m.membershipId) : [...value, m.membershipId])}>
                <input type="checkbox" readOnly checked={value.includes(m.membershipId)} aria-label={m.name} />
                <Text size="sm">{m.name}</Text>
              </Group>
            ))}
        </Stack>
      </Paper>
    </Stack>
  );
}

function InviteModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const invite = useInviteMembers(ctx.workspaceId);
  const onError = useApiError();
  const [emails, setEmails] = useState('');
  const [result, setResult] = useState<Schemas['Invitation'][] | null>(null);

  const close = () => {
    setEmails('');
    setResult(null);
    onClose();
  };

  return (
    <Modal opened={opened} onClose={close} title={t('team.invite')} size="lg">
      {result ? (
        <Stack>
          <Text size="sm">{t('team.invitationLinks')}</Text>
          {result.map((r) => (
            <Group key={r.membershipId} justify="space-between" wrap="nowrap">
              <Text size="sm" truncate>
                {r.email}
              </Text>
              <CopyButton value={r.invitationUrl}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? t('common.copied') : t('team.copyLink')}>
                    <Button size="xs" variant="light" onClick={copy} leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}>
                      {t('team.copyLink')}
                    </Button>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          ))}
          {result.length === 0 && <Text c="dimmed">{t('team.alreadyMembers')}</Text>}
          <Button onClick={close}>{t('common.done')}</Button>
        </Stack>
      ) : (
        <Stack>
          <Textarea
            label={t('team.emails')}
            description={t('team.emailsHint')}
            autosize
            minRows={3}
            value={emails}
            onChange={(e) => setEmails(e.currentTarget.value)}
            data-autofocus
          />
          <Button
            loading={invite.isPending}
            disabled={!emails.trim()}
            onClick={() =>
              invite.mutate(
                { emails: emails.split(/[\s,;]+/).filter((e) => e.includes('@')) },
                { onSuccess: setResult, onError },
              )
            }
          >
            {t('team.sendInvitations')}
          </Button>
        </Stack>
      )}
    </Modal>
  );
}
